import React, { useMemo } from 'react';

import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Form, Formik } from 'formik';
import { useSelector } from 'react-redux';
import { IntlShape, useIntl } from 'react-intl';

import TextField from '@/components/form/text/TextField';
import Tooltip from '@/components/Tooltip';
import { useBankIdProfile } from '@/hooks/useBankIdProfile';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { doNothing } from '@/utils/utils';
import { selectFastRegMxV2Tag, selectIsFastRegThDone } from '@/store/selectors/userSelectors';
import { BffUser } from '@lucky7ventures/bff-types';
import { envIsMexico } from '@/constants/constants';

type FieldConfig = {
  id: string;
  value: string;
  labelIntlId: string;
  isDisplayed: boolean;
  fieldClassName?: string;
};

type FieldsConfigs = Array<FieldConfig>;

const getFormattedAddress = (user: BffUser): string => {
  return `${user.address}${user.houseNumber != null ? ' ' + user.houseNumber : ''}, ${user.city}`;
};
const getUserBirthdateMeta = (user: BffUser) => {
  const birthDateValues = user.birthDate.toString().split('-');
  const birthYear = birthDateValues[0];
  const birthMonth = birthDateValues[1];
  const birthDay = birthDateValues[2].split('T')[0];

  return { birthYear, birthMonth, birthDay };
};

const getFieldsConfigsMx = (user: BffUser, hasInputMobileNumber: boolean): FieldsConfigs => {
  const { birthYear, birthMonth, birthDay } = getUserBirthdateMeta(user);

  return [
    {
      id: 'fullName',
      value: `${user.firstName} ${user.lastName}`,
      labelIntlId: 'inputs.fullName',
      isDisplayed: true,
    },
    {
      id: 'dateOfBirthYear',
      value: birthYear,
      labelIntlId: 'inputs.year',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'dateOfBirthMonth',
      value: birthMonth,
      labelIntlId: 'inputs.month',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'dateOfBirthDay',
      value: birthDay,
      labelIntlId: 'inputs.day',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'mobile',
      value: `${user.mobilePrefix} ${user.mobile}`,
      labelIntlId: 'inputs.number',
      isDisplayed: hasInputMobileNumber,
    },
    {
      id: 'email',
      value: user.email,
      labelIntlId: 'inputs.email',
      isDisplayed: true,
    },
  ];
};

const getFieldsConfigs = (
  user: BffUser,
  isFastRegThDone: boolean,
  isCompletedBankIdProfile: boolean,
): FieldsConfigs => {
  const { birthYear, birthMonth, birthDay } = getUserBirthdateMeta(user);

  return [
    {
      id: 'fullName',
      value: `${user.firstName} ${user.lastName}`,
      labelIntlId: 'inputs.fullName',
      isDisplayed: true,
    },
    {
      id: 'dateOfBirthYear',
      value: birthYear,
      labelIntlId: 'inputs.year',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'dateOfBirthMonth',
      value: birthMonth,
      labelIntlId: 'inputs.month',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'dateOfBirthDay',
      value: birthDay,
      labelIntlId: 'inputs.day',
      isDisplayed: true,
      fieldClassName: 'sm:!col-auto',
    },
    {
      id: 'address',
      value: getFormattedAddress(user),
      labelIntlId: 'inputs.address',
      isDisplayed: !isFastRegThDone,
    },
    {
      id: 'country',
      value: user.countryName,
      labelIntlId: 'inputs.country',
      isDisplayed: !isFastRegThDone,
      fieldClassName: 'sm:!col-span-2',
    },
    {
      id: 'postalCode',
      value: user.postalCode,
      labelIntlId: 'inputs.postcode',
      isDisplayed: !isFastRegThDone,
      fieldClassName: 'sm:!col-span-1',
    },
    {
      id: 'mobile',
      value: `${user.mobilePrefix} ${user.mobile}`,
      labelIntlId: 'inputs.number',
      isDisplayed: isCompletedBankIdProfile,
    },
    {
      id: 'email',
      value: user.email,
      labelIntlId: 'inputs.email',
      isDisplayed: isCompletedBankIdProfile && !isFastRegThDone,
    },
  ];
};

const renderField = (config: FieldConfig, intl: IntlShape) => {
  return (
    <div className={config.fieldClassName} key={config.id}>
      <TextField
        disabled
        value={config.value}
        name={config.id}
        label={intl.formatMessage({ id: config.labelIntlId })}
        className="border-[1px] border-gray-300 font-bold"
        labelClassName="text-blue-medium"
        endIcon={
          <Tooltip
            position="left"
            text={intl.formatMessage({
              id: 'inputs.locked.tooltip',
            })}
            className="max-w-[220px] sm:max-w-max"
          >
            <LockClosedIcon className="w-5 h-5 text-black" />
          </Tooltip>
        }
      />
    </div>
  );
};

const UserDetailsFields = ({ user }: { user: BffUser }) => {
  const intl = useIntl();
  const { isCompletedBankIdProfile } = useBankIdProfile();
  const locale = useSelector(selectLocale);
  const isFastRegThDone = useSelector(selectIsFastRegThDone);
  const isFastRegMxV2TagPresent = useSelector(selectFastRegMxV2Tag);
  const isMexico = envIsMexico();

  const { birthYear, birthMonth, birthDay } = getUserBirthdateMeta(user);

  const initialValues = {
    fullName: `${user.firstName} ${user.lastName}`,
    dateOfBirthYear: birthYear,
    dateOfBirthMonth: birthMonth,
    dateOfBirthDay: birthDay,
    address: getFormattedAddress(user),
    country: user.countryName,
    postalCode: user.postalCode,
    mobile: `${user.mobilePrefix} ${user.mobile}`,
    email: user.email,
  };

  const fields = useMemo(
    () =>
      (isMexico
        ? getFieldsConfigsMx(user, !isFastRegMxV2TagPresent)
        : getFieldsConfigs(user, isFastRegThDone, isCompletedBankIdProfile)
      )
        .filter(field => field.isDisplayed)
        .map(field => renderField(field, intl)),
    [locale, user, isCompletedBankIdProfile],
  );

  return (
    <>
      <h2 className="w-full mt-0 text-[22px] mb-8">
        {intl.formatMessage({
          id: 'settings.details.personalData.header',
        })}
      </h2>
      <Formik initialValues={initialValues} onSubmit={doNothing} enableReinitialize>
        <Form className="w-full grid grid-cols-1 sm:grid-cols-3 gap-5 [&>div]:sm:col-span-3">
          {fields}
        </Form>
      </Formik>
    </>
  );
};

export default UserDetailsFields;
